/**
 * @file Form label component.
 */

import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';

import InfoCircleIcon from '../icons/InfoCircleIcon';

const propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node,
  labelNote: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelTip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelTipPlace: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  labelType: PropTypes.oneOf(['standard', 'expanded', 'light']),
  labelSrOnly: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  size: PropTypes.oneOf(['sm', 'md']),
  required: PropTypes.bool,
  optional: PropTypes.bool,
  error: PropTypes.bool,
};

const defaultProps = {
  labelTipPlace: 'top',
};

const Label = ({
  id,
  label,
  labelNote,
  labelTip,
  labelTipPlace,
  labelSrOnly,
  labelType,
  className,
  size,
  children,
  required,
  optional,
  error,
}) => {
  const classes = classNames({
    label: true,
    'label-expanded': labelType === 'expanded',
    'label-light': labelType === 'light',
    'label-error': !!error,
    'sr-only': labelSrOnly,
    [`label-${size}`]: !!size,
    [className]: !!className,
  });

  const LabelTip = () => (
    <span
      className="label-tip"
      data-for={`tooltip-${id}`}
      data-tip=""
      data-event="mouseenter click"
      data-effect="solid"
      data-place={labelTipPlace}
    >
      <InfoCircleIcon size={15} />

      <ReactTooltip
        id={`tooltip-${id}`}
        className="tooltip"
        globalEventOff="click"
      >
        {labelTip}
      </ReactTooltip>
    </span>
  );

  return (
    <span className={classes}>
      <label htmlFor={id} id={`label-for-${id}`} className="label-text">
        {children || label}

        {required && labelType === 'expanded' && (
          <span className="label-required">*</span>
        )}
        {optional && (
          <span className="text-primary text-bold text-xs ml-1">optional</span>
        )}
        {labelNote && (
          <span className="label-complement">
            {labelNote} {labelTip && <LabelTip />}
          </span>
        )}

        {labelTip && !labelNote && <LabelTip />}
      </label>
    </span>
  );
};

Label.propTypes = propTypes;
Label.defaultProps = defaultProps;

export default Label;
